// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { CartContext } from "@insite/client-framework/Components/CartContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import displayOrder from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/DisplayOrder";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { PageErrorWrapper } from "@insite/content-library/Components/PageErrorWrapper";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Page from "@insite/mobius/Page";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let cartId;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ cartId: string }>(location.search);
        cartId = parsedQuery.cartId;
    }

    const savedOrderState = getCartState(state, cartId);

    return {
        cartId,
        savedCartState: savedOrderState,
        billToState: getBillToState(state, savedOrderState?.value?.billToId),
        shipToState: getShipToState(state, savedOrderState?.value?.shipToId),
        savedOrderListPageLink: getPageLinkByPageType(state, "SavedOrderListPage")?.url,
        // xng-change
        isLoading: savedOrderState.isLoading,
    };
};

const mapDispatchToProps = {
    displayOrder,
};

export interface SavedOrderDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
     // xng-change
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const savedOrderDetailsPageStyles: SavedOrderDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: { weight: "bold" },
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;
const styles = savedOrderDetailsPageStyles;

const SavedOrderDetailsPage = ({
    id,
    cartId,
    savedCartState,
    displayOrder,
    billToState,
    shipToState,
    history,
    savedOrderListPageLink,
    // xng-change
    isLoading,
}: Props) => {
    useEffect(() => {
        if (cartId && savedOrderListPageLink) {
            displayOrder({
                cartId,
                onError: () => {
                    history.push(savedOrderListPageLink);
                },
            });
        }
    }, [cartId, savedOrderListPageLink]);

    const savedOrder = savedCartState.value
        ? {
              ...savedCartState.value,
              billTo: billToState.value,
              shipTo: shipToState.value,
          }
        : undefined;

    // xng-change -showing spinner
    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        )
    }

    return (
        <Page>
            <PageErrorWrapper
                value={savedCartState}
                extendedStyles={styles}
                notFoundMessage={siteMessage("Cart_CartNotFound")}
            >
                <CartContext.Provider value={savedOrder}>
                    <Zone contentId={id} zoneName="Content" />
                    <Modals />
                </CartContext.Provider>
            </PageErrorWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SavedOrderDetailsPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "SavedOrderDetailsPage" instead of this constant.
 */
export const SavedOrderDetailsPageContext = "SavedOrderDetailsPage";
